import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { feedService } from '../services/api';

// Query keys
export const feedKeys = {
  all: ['feed'],
  wallFeeds: (page) => [...feedKeys.all, 'wall-feeds', page],
  userProfile: (username) => [...feedKeys.all, 'user-profile', username],
  followers: (userId) => [...feedKeys.all, 'followers', userId],
  following: (userId) => [...feedKeys.all, 'following', userId],
  searchUsers: (query) => [...feedKeys.all, 'search-users', query],
};

// Hook for infinite wall feeds
export const useWallFeeds = () => {
  return useInfiniteQuery({
    queryKey: feedKeys.wallFeeds(),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await feedService.getWallFeeds(pageParam, 10);
      return {
        data: response.data.data.posts || [],
        nextPage: pageParam + 1,
        hasMore: response.data.data.pagination?.hasMore || false,
      };
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextPage : undefined,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Hook for user profile
export const useUserProfile = (username) => {
  return useQuery({
    queryKey: feedKeys.userProfile(username),
    queryFn: async () => {
      const response = await feedService.getUserProfile(username);
      return response.data.user;
    },
    enabled: !!username,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for followers
export const useFollowers = (userId) => {
  return useQuery({
    queryKey: feedKeys.followers(userId),
    queryFn: async () => {
      const response = await feedService.getFollowers(userId);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for following
export const useFollowing = (userId) => {
  return useQuery({
    queryKey: feedKeys.following(userId),
    queryFn: async () => {
      const response = await feedService.getFollowing(userId);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for creating a post
export const useCreatePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (formData) => {
      console.log("useCreatePost - Calling feedService.createPost with:", formData);
      const result = await feedService.createPost(formData);
      console.log("useCreatePost - API result:", result);
      return result;
    },
    onSuccess: (data) => {
      console.log("useCreatePost - Success:", data);
      // Invalidate wall feeds to show new post
      queryClient.invalidateQueries({ queryKey: feedKeys.wallFeeds() });
    },
    onError: (error) => {
      console.error("useCreatePost - Error:", error);
    },
  });
};

// Hook for liking/unliking a post
export const useLikePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (postId) => {
      return feedService.likeUnlikePost(postId);
    },
    onMutate: async (postId) => {
      
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: feedKeys.wallFeeds() });
      
      // Also cancel user posts queries
      await queryClient.cancelQueries({ 
        predicate: (query) => {
          return query.queryKey[0] === 'feed' && 
                 query.queryKey[1] === 'user-posts';
        }
      });

      // Snapshot the previous values
      const previousWallData = queryClient.getQueryData(feedKeys.wallFeeds());
      const previousUserPostsData = {};

      // Update wall feeds
      queryClient.setQueryData(feedKeys.wallFeeds(), (old) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            data: page.data.map(post => {
              const currentPostId = post.postId || post._id || post.id;
              if (currentPostId === postId) {
                const currentCount = post.likesCount || 0;
                const currentIsLiked = post.isLiked === undefined ? false : post.isLiked;
                
                return {
                  ...post,
                  isLiked: !currentIsLiked,
                  likesCount: currentIsLiked ? currentCount - 1 : currentCount + 1
                };
              }
              return post;
            })
          }))
        };
      });

      // Update all user posts queries
      const userPostsQueries = queryClient.getQueryCache().findAll({
        predicate: (query) => {
          return query.queryKey[0] === 'feed' && 
                 query.queryKey[1] === 'user-posts';
        }
      });
      
      userPostsQueries.forEach((query) => {
        const userId = query.queryKey[2];
        previousUserPostsData[userId] = queryClient.getQueryData(query.queryKey);
        
        queryClient.setQueryData(query.queryKey, (old) => {
          if (!old) return old;
          
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              data: page.data.map(post => {
                const currentPostId = post.postId || post._id || post.id;
                if (currentPostId === postId) {
                  const currentCount = post.likesCount || 0;
                  const currentIsLiked = post.isLiked === undefined ? false : post.isLiked;
                  
                  return {
                    ...post,
                    isLiked: !currentIsLiked,
                    likesCount: currentIsLiked ? currentCount - 1 : currentCount + 1
                  };
                }
                return post;
              })
            }))
          };
        });
      });

      // Return a context object with the snapshotted values
      return { previousWallData, previousUserPostsData };
    },
    onError: (err, postId, context) => {
      
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousWallData) {
        queryClient.setQueryData(feedKeys.wallFeeds(), context.previousWallData);
      }
      
      // Rollback user posts data
      if (context?.previousUserPostsData) {
        Object.entries(context.previousUserPostsData).forEach(([userId, data]) => {
          queryClient.setQueryData(['feed', 'user-posts', userId], data);
        });
      }
      
      // Only refetch on error to get the correct server state
      queryClient.invalidateQueries({ queryKey: feedKeys.wallFeeds() });
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          return query.queryKey[0] === 'feed' && 
                 query.queryKey[1] === 'user-posts';
        }
      });
    },
    onSuccess: (data, postId) => {
      // Don't refetch on success - keep our optimistic update
    },
  });
};

// Hook for commenting on a post with optimistic updates
export const useCommentOnPost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ postId, content }) => feedService.commentOnPost(postId, content),
    onMutate: async ({ postId, content, currentUser }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [...feedKeys.all, 'post-comments', postId] });
      await queryClient.cancelQueries({ queryKey: feedKeys.wallFeeds() });

      // Snapshot the previous values
      const previousComments = queryClient.getQueryData([...feedKeys.all, 'post-comments', postId]);
      const previousFeeds = queryClient.getQueryData(feedKeys.wallFeeds());

      // Create optimistic comment
      const optimisticComment = {
        _id: `temp-${Date.now()}`, // Temporary ID
        content,
        user: {
          _id: currentUser._id || currentUser.id,
          userName: currentUser.userName,
          profilePicture: currentUser.profilePicture
        },
        timeAgo: 'now',
        createdAt: new Date().toISOString()
      };

      // Optimistically update comments list
      queryClient.setQueryData([...feedKeys.all, 'post-comments', postId], (old) => {
        if (!old) {
          return {
            pages: [{
              comments: [optimisticComment],
              hasMore: false,
              totalCount: 1
            }],
            pageParams: [1]
          };
        }
        
        return {
          ...old,
          pages: old.pages.map((page, index) => {
            if (index === 0) { // Add to first page
              return {
                ...page,
                comments: [optimisticComment, ...page.comments],
                totalCount: (page.totalCount || 0) + 1
              };
            }
            return page;
          })
        };
      });

      // Optimistically update post comment count
      queryClient.setQueryData(feedKeys.wallFeeds(), (old) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            data: page.data.map(post => {
              const currentPostId = post.postId || post._id || post.id;
              if (currentPostId === postId) {
                return {
                  ...post,
                  commentsCount: (post.commentsCount || 0) + 1
                };
              }
              return post;
            })
          }))
        };
      });

      // Return context for rollback
      return { previousComments, previousFeeds, optimisticComment };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousComments) {
        queryClient.setQueryData([...feedKeys.all, 'post-comments', variables.postId], context.previousComments);
      }
      if (context?.previousFeeds) {
        queryClient.setQueryData(feedKeys.wallFeeds(), context.previousFeeds);
      }
    },
    onSuccess: (data, variables, context) => {
      // Invalidate and refetch comments to get the fresh data from server
      queryClient.invalidateQueries({ 
        queryKey: [...feedKeys.all, 'post-comments', variables.postId] 
      });
      
      // Also update the post's comment count in the wall feeds
      queryClient.setQueryData(feedKeys.wallFeeds(), (old) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            data: page.data.map(post => {
              const currentPostId = post.postId || post._id || post.id;
              if (currentPostId === variables.postId) {
                return {
                  ...post,
                  commentsCount: data.data.post.commentsCount // Use updated count from server
                };
              }
              return post;
            })
          }))
        };
      });
    },
  });
};

// Hook for getting post comments with pagination
export const usePostComments = (postId) => {
  return useInfiniteQuery({
    queryKey: [...feedKeys.all, 'post-comments', postId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await feedService.getPostComments(postId, pageParam, 10);
      return {
        comments: response.data.data.comments || [],
        nextPage: pageParam + 1,
        hasMore: response.data.data.pagination?.hasMore || false,
        totalCount: response.data.data.pagination?.total || 0
      };
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextPage : undefined,
    enabled: !!postId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Hook for deleting a post
export const useDeletePost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: feedService.deletePost,
    onSuccess: () => {
      // Invalidate wall feeds to remove deleted post
      queryClient.invalidateQueries({ queryKey: feedKeys.wallFeeds() });
    },
  });
};

// Hook for searching users
export const useSearchUsers = (query) => {
  return useQuery({
    queryKey: feedKeys.searchUsers(query),
    queryFn: async () => {
      const response = await feedService.searchUsers(query);
      // The API returns {data: {user: {...}}} but component expects array
      if (response.data.data && response.data.data.user) {
        return [response.data.data.user]; // Wrap single user in array
      }
      return []; // Return empty array if no user found
    },
    enabled: !!query && query.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for getting user posts
export const useUserPosts = (userId) => {
  return useInfiniteQuery({
    queryKey: [...feedKeys.all, 'user-posts', userId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await feedService.getUserPosts(userId, pageParam);
      return {
        data: response.data.data.posts || [],
        nextPage: pageParam + 1,
        hasMore: response.data.data.pagination?.hasMore || false,
      };
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextPage : undefined,
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Hook for follow stats
export const useFollowStats = (userId) => {
  return useQuery({
    queryKey: [...feedKeys.all, 'follow-stats', userId],
    queryFn: async () => {
      const response = await feedService.getFollowStats(userId);
      return response.data.data; // Access the nested data object
    },
    enabled: !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Hook for follow status
export const useFollowStatus = (userId) => {
  return useQuery({
    queryKey: [...feedKeys.all, 'follow-status', userId],
    queryFn: async () => {
      const response = await feedService.getFollowStatus(userId);
      return response.data.data.isFollowing; // Access the nested data object and isFollowing property
    },
    enabled: !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Hook for following/unfollowing a user
export const useFollowUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: feedService.followUser,
    onSuccess: (data, variables) => {
      // Invalidate follow status and stats
      queryClient.invalidateQueries({ 
        queryKey: [...feedKeys.all, 'follow-status', variables.followingId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...feedKeys.all, 'follow-stats', variables.followingId] 
      });
    },
  });
}; 